import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiShield, FiClock, FiMapPin, FiStar } from 'react-icons/fi';
// import { useTranslation } from 'react-i18next';

const HeaderBar = styled.header`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background: rgba(218, 1, 92, 0.92);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  height: 64px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  @media (max-width: 600px) {
    padding: 0 10px;
    height: 56px;
  }
`;

const NavbarFlexRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 1px;
  line-height: 40px;
  img {
    height: 20px;
    margin-left: 8px;
    max-width: 120px;
    width: auto;
    object-fit: contain;
    display: block;
    align-self: center;
    vertical-align: middle;
    @media (max-width: 600px) {
      height: 16px;
      margin-left: 5px;
      max-width: 80px;
    }
  }
`;

// BrandRow and BrandName for hero branding
const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  gap: 10px;
`;

const BrandName = styled.span`
  color: #DA015C;
  font-family: inherit;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 1.3rem;
  line-height: 1.1;
  text-shadow: 0 1px 4px rgba(44, 62, 80, 0.08);
`;

const RegisterButton = styled.a`
  background: #fff;
  color: #DA015C;
  font-weight: 700;
  border-radius: 24px;
  padding: 0 20px;
  height: 40px;
  line-height: 40px;
  font-size: 1rem;
  text-decoration: none;
  margin-left: 14px;
  transition: background 0.2s, color 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 600px) {
    padding: 0 10px;
    height: 32px;
    line-height: 32px;
    font-size: 0.95rem;
    border-radius: 18px;
    margin-left: 6px;
  }
  &:hover {
    background: #ffe0f0;
    color: #b80149;
  }
`;


const HeroSection = styled.section`
  height: 100vh;
  width: 100%;
  background: ${props => props.theme.colors.gradient};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 80px; /* Add padding for navbar */
  @media (max-width: 600px) {
    padding-top: 88px;
    padding-left: 10px;
    padding-right: 10px;
    height: auto;
    min-height: 100vh;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 0 20px;
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;
  align-items: center;
  height: 100%;
  z-index: 2;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
    text-align: center;
    height: auto;
  }
`;

const GlassCard = styled.div`
  background: none;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
  margin-bottom: 0;
  width: 100%;
  max-width: 600px;
  backdrop-filter: none;
  position: relative;
  min-height: 260px;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0;
    margin-bottom: 0;
    min-height: 180px;
    height: auto;
  }
`;

const DesktopHighlightsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 28px;
  margin-top: 2rem;
  margin-bottom: 2rem;
  align-items: stretch;
  @media (max-width: 600px) {
    display: none;
  }
`;

const MobileHighlightsRow = styled.div`
  display: none;
  @media (max-width: 600px) {
    display: flex;
    flex-direction: row;
    gap: 18px;
    margin-top: 2.2rem;
    margin-bottom: 0.8rem;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
`;
const ScrollDownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  margin-top: 3vh;
  cursor: pointer;
  width: fit-content;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.7;
  }
`;
const ScrollText = styled.span`
  font-size: 1.1rem;
  color: #fff;
  font-weight: 600;
  margin-bottom: 4px;
`;
const ScrollArrow = styled.span`
  font-size: 2.2rem;
  color: #000000;
  text-shadow: 0 0 8px #fff, 0 0 12px #fff, 0 0 16px #fff;
  animation: bounce 1.2s infinite;
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(8px); }
  }
`;

// Legacy, safe to remove after migration
const HighlightsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 28px;
  margin-top: 2rem;
  margin-bottom: 2rem;
  align-items: stretch;
  @media (max-width: 600px) {
    flex-direction: row;
    gap: 18px;
    margin-top: 2.2rem;
    margin-bottom: 0.8rem;
    align-items: center;
  }
`;

const Highlight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  direction: rtl;
`;

const HighlightIcon = styled.div`
  background: none;
  color: #fff;
  border-radius: 0;
  padding: 0;
  font-size: 2.4rem;
  margin-top: 2px;
  box-shadow: none;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  text-shadow: 0 0 8px #DA015C, 0 0 16px #DA015C, 0 0 32px #DA015C, 0 0 40px #DA015C;
  @media (max-width: 600px) {
    font-size: 2.8rem;
    margin-top: 2px;
  }
`;

const HighlightText = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.dark || '#000000'};
  font-weight: 600;
  text-align: center;
`;

const PhoneImage = styled(motion.img)`
  width: auto;
  height: 90%;
  max-height: 650px;
  object-fit: contain;
  position: relative;
  z-index: 2;
  border-radius: 24px;
  border: 4px solid #fff;
  box-shadow: 0 16px 40px rgba(26,127,100,0.15);
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 100%;
    max-width: 450px;
    height: auto;
  }
`;

const TextContent = styled.div`
  max-width: 600px;
  text-align: right; /* RTL: Ensure text is right-aligned */
  direction: rtl;
  @media (max-width: 600px) {
    margin-bottom: 30px;
    text-align: center;
    direction: rtl;
  }
`;


const Title = styled(motion.h1)`
  font-size: 5rem;
  font-weight: 800;
  color: ${props => props.theme.colors.white};
  margin-bottom: 1.5rem;
  line-height: 1.1;
  text-align: right;
  direction: rtl;
  @media (max-width: 600px) {
    font-size: 2.1rem;
    margin-top: 18px;
    margin-bottom: 1.1rem;
    text-align: center;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.white};
  margin-bottom: 2.2rem;
  line-height: 1.5;
  text-align: right;
  direction: rtl;
  @media (max-width: 600px) {
    font-size: 1.1rem;
    margin-bottom: 1.4rem;
    text-align: center;
  }
`;

export const ButtonsWrapper = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  text-align: center;
  @media (max-width: 600px) {
    justify-content: center;
    align-items: center;
    width: 100%;
    text-align: center;
  }
`;

export const PrimaryButton = styled(motion.a)`
  background: #DA015C;
  color: #fff;
  padding: 0.8rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  &:hover, &:focus {
    background: #b80149;
    color: #fff;
    outline: 2px solid #DA015C;
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled(motion.a)`
  background-color: transparent;
  color: ${props => props.theme.colors.white};
  padding: 0.8rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  border: 2px solid ${props => props.theme.colors.white};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ImageContent = styled.div`
  position: relative;
  width: 50%; /* Increase width */
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 70vh; /* Make taller to match screenshot */
  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
    align-items: center;
    height: auto;
    margin-bottom: 18px;
  }
`;


const BackgroundElement = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.1);
  clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%);
  z-index: 1;
`;

const Hero: React.FC = () => (
  <>

    <HeroSection id="home">
      <BackgroundElement />
      <ContentWrapper>
        <TextContent>
          <GlassCard>
            <BrandRow style={{ marginTop: '28px' }}>
              <BrandName style={{ fontSize: '1.3rem', fontWeight: 700, opacity: 0 }}>Wassel</BrandName>
            </BrandRow>
            
            <div style={{ fontSize: '2.2rem', fontWeight: 800, marginTop: '14px', textAlign: 'center', color: '#fff', lineHeight: 1.25 }}>
              استكشف وسيلتك الجديدة للتنقل
              <span style={{ display: 'block', fontSize: '1.1em', fontWeight: 600, marginTop: 2 }}>مع واصل في منطقتك</span>
            </div>
            <div style={{ marginTop: '14px', color: '#fff', fontSize: '1.08rem', textAlign: 'center' }}>
              واصل هو أول تطبيق لحجز المشاوير في المدن والأحياء العربية. احجز رحلتك بسهولة وأمان، أينما كنت.
            </div>
            <div style={{ marginTop: '20px', marginBottom: '12px', fontSize: '1rem', color: '#000000', fontWeight: 600, textAlign: 'center' }}>
              اختر طريقتك للانضمام:
            </div>
            <ButtonsWrapper
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              style={{ flexDirection: 'row', gap: 16, marginBottom: 28 }}
            >
              <PrimaryButton
                href="#waitlist"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ background: '#000000', color: '#fff', border: '2px solid #000000', fontWeight: 700 }}
                onClick={e => {
                  e.preventDefault();
                  document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
                  window.dispatchEvent(new CustomEvent('selectWaitlistTab', { detail: 'driver' }));
                }}
              >
                سجّل كسائق
              </PrimaryButton>
              <PrimaryButton
                href="#waitlist"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ background: '#fff', color: '#DA015C', border: '2px solid #DA015C', fontWeight: 700 }}
                onClick={e => {
                  e.preventDefault();
                  document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
                  window.dispatchEvent(new CustomEvent('selectWaitlistTab', { detail: 'rider' }));
                }}
              >
                سجّل كمستخدم
              </PrimaryButton>
            </ButtonsWrapper>
            <DesktopHighlightsRow style={{ marginTop: 20, marginBottom: 16 }}>
              <Highlight style={{ background: '#f4f8fa', borderRadius: 24, padding: '14px 22px', margin: '0 8px', display: 'flex', alignItems: 'center' }}>
                <HighlightIcon style={{ fontSize: '1.85rem', background: '#e6f7f0', borderRadius: '50%', padding: 10 }}><FiShield /></HighlightIcon>
                <HighlightText style={{ fontWeight: 700, marginRight: 8 }}>آمن وموثوق</HighlightText>
              </Highlight>
              <Highlight style={{ background: '#f4f8fa', borderRadius: 24, padding: '14px 22px', margin: '0 8px', display: 'flex', alignItems: 'center' }}>
                <HighlightIcon style={{ fontSize: '1.85rem', background: '#e6f7f0', borderRadius: '50%', padding: 10 }}><FiClock /></HighlightIcon>
                <HighlightText style={{ fontWeight: 700, marginRight: 8 }}>سريع الاستجابة</HighlightText>
              </Highlight>
              <Highlight style={{ background: '#f4f8fa', borderRadius: 24, padding: '14px 22px', margin: '0 8px', display: 'flex', alignItems: 'center' }}>
                <HighlightIcon style={{ fontSize: '1.85rem', background: '#e6f7f0', borderRadius: '50%', padding: 10 }}><FiMapPin /></HighlightIcon>
                <HighlightText style={{ fontWeight: 700, marginRight: 8 }}>تغطية واسعة</HighlightText>
              </Highlight>
            </DesktopHighlightsRow>
          </GlassCard>
        </TextContent>
        <ImageContent>
          <PhoneImage
            src="/images/wassel-app-screen.png"
            alt="Wassel App"
            style={{ width: '120%', maxWidth: 340, margin: '0 auto', marginTop: 0, marginBottom: 24, boxShadow: '0 8px 32px 0 rgba(44, 62, 80, 0.18)' }}
          />
        </ImageContent>
        <MobileHighlightsRow style={{ marginTop: 20 }}>
          <Highlight>
            <HighlightIcon><FiShield /></HighlightIcon>
            <HighlightText>آمن وموثوق</HighlightText>
          </Highlight>
          <Highlight>
            <HighlightIcon><FiClock /></HighlightIcon>
            <HighlightText>سريع الاستجابة</HighlightText>
          </Highlight>
          <Highlight>
            <HighlightIcon><FiMapPin /></HighlightIcon>
            <HighlightText>تغطية واسعة</HighlightText>
          </Highlight>
        </MobileHighlightsRow>
        <ScrollDownWrapper onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
          <ScrollText>اعرف المزيد</ScrollText>
          <ScrollArrow>↓</ScrollArrow>
        </ScrollDownWrapper>
      </ContentWrapper>
    </HeroSection>
  </>
);

export default Hero;